import { formatExternalApiDateTime } from "@/Utile/ncrmExternalApi.mjs";
import {
  fetchPlatformEmailSettings,
  savePlatformEmailSettings,
  sendPlatformEmailTest,
  testPlatformEmailConnection,
} from "./platformEmailApi.mjs";

export const createEmptyPlatformEmailForm = () => ({
  enabled: false,
  host: "",
  port: 587,
  secure: false,
  requireTLS: true,
  authType: "password",
  username: "",
  password: "",
  fromEmail: "",
  fromName: "MiriDar",
  replyToEmail: "",
  adminNotificationEmail: "",
});

const applySettings = (ctx, settings) => {
  ctx.settings = settings || null;
  const base = createEmptyPlatformEmailForm();
  ctx.form = settings
    ? {
        ...base,
        enabled: Boolean(settings.enabled),
        host: settings.host || "",
        port: Number(settings.port) || 587,
        secure: Boolean(settings.secure),
        requireTLS: Boolean(settings.requireTLS),
        authType: settings.authType || "password",
        username: settings.username || "",
        fromEmail: settings.fromEmail || "",
        fromName: settings.fromName || "",
        replyToEmail: settings.replyToEmail || "",
        adminNotificationEmail: settings.adminNotificationEmail || "",
      }
    : base;
};

export default {
  data() {
    return {
      loading: false,
      saving: false,
      testing: false,
      sendingTest: false,
      settings: null,
      form: createEmptyPlatformEmailForm(),
      testRecipient: "",
      errorMessage: "",
      successMessage: "",
    };
  },
  computed: {
    authTypeItems() {
      return [
        { title: this.$t("admin.platformEmail.smtp.auth.password"), value: "password" },
        { title: this.$t("admin.platformEmail.smtp.auth.none"), value: "none" },
      ];
    },
    hasSavedSettings() {
      return Boolean(this.settings?.id);
    },
    testStatusColor() {
      if (this.settings?.lastTestStatus === "ok") return "success";
      if (this.settings?.lastTestStatus === "error") return "error";
      return "secondary";
    },
    testStatusLabel() {
      return this.$t(`admin.platformEmail.smtp.testStatuses.${this.settings?.lastTestStatus || "unknown"}`);
    },
    lastTestedLabel() {
      if (!this.settings?.lastTestedAt) return this.$t("admin.platformEmail.smtp.neverTested");
      return formatExternalApiDateTime(this.settings.lastTestedAt, "ro-RO");
    },
  },
  mounted() {
    this.loadSettings();
  },
  methods: {
    clearFeedback() {
      this.errorMessage = "";
      this.successMessage = "";
    },
    async loadSettings() {
      this.loading = true;
      this.clearFeedback();
      try {
        const { data } = await fetchPlatformEmailSettings();
        applySettings(this, data?.settings || null);
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("admin.platformEmail.errors.loadSettings");
      } finally {
        this.loading = false;
      }
    },
    async saveSettings() {
      this.saving = true;
      this.clearFeedback();
      try {
        const payload = { ...this.form };
        if (!payload.password) delete payload.password;
        const { data } = await savePlatformEmailSettings(payload);
        applySettings(this, data?.settings || null);
        this.successMessage = this.$t("admin.platformEmail.messages.settingsSaved");
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("admin.platformEmail.errors.saveSettings");
      } finally {
        this.saving = false;
      }
    },
    async testConnection() {
      this.testing = true;
      this.clearFeedback();
      try {
        const { data } = await testPlatformEmailConnection();
        applySettings(this, data?.settings || this.settings);
        this.successMessage = this.$t("admin.platformEmail.messages.connectionOk");
      } catch (error) {
        const message = error?.response?.data?.error || this.$t("admin.platformEmail.errors.testConnection");
        try {
          const { data } = await fetchPlatformEmailSettings();
          applySettings(this, data?.settings || null);
        } catch {
          // Păstrăm eroarea testului ca feedback principal; reîncărcarea este best-effort.
        }
        this.errorMessage = message;
      } finally {
        this.testing = false;
      }
    },
    async sendTestEmail() {
      this.sendingTest = true;
      this.clearFeedback();
      try {
        await sendPlatformEmailTest(this.testRecipient);
        this.successMessage = this.$t("admin.platformEmail.messages.testEmailSent");
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("admin.platformEmail.errors.sendTest");
      } finally {
        this.sendingTest = false;
      }
    },
  },
};
